// DWC-154 Writer — new draft form scoped to a Case.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, drafts } from "../lib/api";
import { pushToast } from "../components/Toast";

const TARGET_TYPES: { value: string; label: string }[] = [
  { value: "designated_doctor", label: "Designated Doctor" },
  { value: "treating_doctor", label: "Treating Doctor" },
  { value: "carrier", label: "Insurance Carrier" },
  { value: "adjuster", label: "Adjuster" },
  { value: "utilization_review_officer", label: "Utilization Review Officer" },
  { value: "peer_reviewer", label: "Peer Reviewer" },
  { value: "rme_doctor", label: "RME Doctor" },
  { value: "other", label: "Other" },
];

export function DraftNewPage(): JSX.Element {
  const params = useParams<{ caseId: string }>();
  const caseId = Number(params.caseId);
  const navigate = useNavigate();
  const [targetType, setTargetType] = useState("designated_doctor");
  const [targetDesc, setTargetDesc] = useState("");
  const [alleged, setAlleged] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [verifierFailure, setVerifierFailure] = useState<{
    offending_citations: string[];
    offending_document_ids: number[];
  } | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!alleged.trim()) return;
    setVerifierFailure(null);
    setSubmitting(true);
    try {
      const d = await drafts.create(caseId, {
        target_type: targetType,
        target_description: targetDesc.trim() || undefined,
        alleged_conduct: alleged.trim(),
      });
      pushToast("success", `Draft #${d.id} ready`);
      navigate(`/drafts/${d.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === "DRAFT_CITATION_UNVERIFIED" && err.details) {
        const oc = Array.isArray(err.details.offending_citations)
          ? (err.details.offending_citations as string[])
          : [];
        const od = Array.isArray(err.details.offending_document_ids)
          ? (err.details.offending_document_ids as number[])
          : [];
        setVerifierFailure({ offending_citations: oc, offending_document_ids: od });
      } else if (err instanceof ApiError && err.code === "NOT_APPLICABLE_JURISDICTION") {
        pushToast(
          "error",
          "The DWC-154 Writer is Texas-only; your tenant is not Texas. Update tenant jurisdiction to TX to use this tool.",
        );
      } else {
        pushToast(
          "error",
          err instanceof ApiError ? err.message : "Draft generation failed",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <nav className="mb-2 text-sm text-slate-500">
        <Link to={`/cases/${caseId}`} className="hover:text-brand-700">
          Case #{caseId}
        </Link>
        <span aria-hidden> / </span>
        <span>New DWC-154 draft</span>
      </nav>
      <h1 className="mb-2 text-2xl font-semibold text-slate-900">DWC-154 Writer</h1>
      <p className="mb-6 max-w-prose-narrow text-sm text-slate-600">
        Generates a Texas DWC-154 complaint draft. The Citation Verifier rejects any
        statutory citation that doesn't resolve to a real section of the ingested Texas Workers'
        Compensation Act; Mode A heuristics and the Phase 6 Cross-Check engine surface defects
        in opposing-party documents associated with this Case.
      </p>
      {verifierFailure && (
        <div className="mb-4 max-w-prose-narrow rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <strong>Citation Verifier rejected the draft.</strong>
          {verifierFailure.offending_citations.length > 0 && (
            <div>
              Unverified citations:{" "}
              <code>{verifierFailure.offending_citations.join(", ")}</code>
            </div>
          )}
          {verifierFailure.offending_document_ids.length > 0 && (
            <div>
              Unknown document ids:{" "}
              <code>{verifierFailure.offending_document_ids.join(", ")}</code>
            </div>
          )}
          <p className="mt-1 text-xs">
            Try adjusting the alleged conduct so the writer anchors to a different section,
            or add the missing source documents to this Case and retry.
          </p>
        </div>
      )}
      <form onSubmit={onSubmit} className="card max-w-prose-narrow space-y-4 p-6">
        <div>
          <label htmlFor="target-type" className="label">
            Target type
          </label>
          <select
            id="target-type"
            className="input"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
          >
            {TARGET_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="target-desc" className="label">
            Target description (optional)
          </label>
          <input
            id="target-desc"
            className="input"
            value={targetDesc}
            onChange={(e) => setTargetDesc(e.target.value)}
            placeholder="e.g. Designated Doctor who issued the denial"
          />
        </div>
        <div>
          <label htmlFor="alleged" className="label">
            Alleged conduct
          </label>
          <textarea
            id="alleged"
            rows={6}
            required
            className="input"
            value={alleged}
            onChange={(e) => setAlleged(e.target.value)}
            placeholder="Describe what the target did. The writer will anchor this to the relevant section of the Texas Workers' Comp Act and surface defects from the documents in this Case."
          />
        </div>
        <div className="flex justify-end gap-2">
          <Link to={`/cases/${caseId}`} className="btn-secondary">
            Cancel
          </Link>
          <button type="submit" className="btn-primary" disabled={submitting || !alleged.trim()}>
            {submitting ? "Generating…" : "Generate draft"}
          </button>
        </div>
      </form>
    </div>
  );
}
